import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  AfterContentInit, Directive,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2
} from "@angular/core";
import { ControlValueAccessor } from "@angular/forms";
import { takeUntil } from "rxjs/operators";

import { DateService } from "@lantern/app/core/utils/date/date.service";
import { Datepicker } from "../../../models/datepicker.model";

@Directive()
export abstract class AbstractDatepickerComponent
  implements ControlValueAccessor, AfterContentInit, OnDestroy {
  // # of months ahead to show
  @Input()
  monthsAhead: number = 0;

  // # of months behind to show
  @Input()
  monthsBehind: number = 0;

  @Input()
  set dates(dates: Datepicker) {
    if (dates == null) {
      return;
    }
    if (
      dates.startDate &&
      dates.endDate &&
      (dates.startDate !== this._dates.startDate ||
        dates.endDate !== this._dates.endDate)
    ) {
      this._dates = dates;
      this.datesChange.emit(this.dates);
    }
  }
  get dates(): Datepicker {
    return this._dates;
  }
  _dates: Datepicker = {
    startDate: this.dateService.currentDate,
    endDate: this.dateService.currentDate
  };
  @Output() datesChange: EventEmitter<Datepicker> = new EventEmitter<
    Datepicker
  >();

  @Input()
  get startInput(): HTMLInputElement | undefined {
    return this._startInput;
  }
  set startInput(input: HTMLInputElement | undefined) {
    this._startInput = input;
    if (this._startInput != null) {
      this.renderer2.setStyle(input, "display", "none");
    }
  }
  private _startInput: HTMLInputElement | undefined;

  @Input()
  get endInput(): HTMLInputElement | undefined {
    return this._endInput;
  }
  set endInput(input: HTMLInputElement | undefined) {
    this._endInput = input;
    if (typeof this._endInput !== "undefined") {
      this.renderer2.setStyle(input, "display", "none");
    }
  }
  private _endInput: HTMLInputElement | undefined;

  // Flag for single date, or date range
  @Input()
  get single(): boolean {
    return this._single;
  }
  set single(value: boolean) {
    value = coerceBooleanProperty(value);
    if (this._single === value) {
      return;
    }
    this._single = value;
  }
  private _single: boolean;

  onDestroyEmitter = new EventEmitter<void>();

  constructor(
    private renderer2: Renderer2,
    protected dateService: DateService
  ) {}

  ngAfterContentInit(): void {
    this.initDatesChangeInputSubscription();
  }

  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  private initDatesChangeInputSubscription(): void {
    this.datesChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((dates) => {
        this.updateInputs(dates);

        if (this._onChangeFn != null) {
          this._onChangeFn(dates);
        }
      });
  }

  private updateInputs({ startDate, endDate }: Datepicker): void {
    if (this.startInput != null) {
      this.setInputValue(this.startInput, startDate);
    }
    if (this.endInput != null) {
      this.setInputValue(this.endInput, endDate);
    }
  }

  private setInputValue(input: HTMLInputElement, value: string): void {
    input.value = value;
    input.dispatchEvent(
      new Event("input", {
        bubbles: true
      })
    );
  }

  writeValue(dates?: Datepicker): void {
    this.dates = {
      startDate: this.dateService.currentDate,
      endDate: this.dateService.currentDate,
      ...dates
    };
  }

  private _onChangeFn?: (value: Datepicker) => void;
  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    return;
  }
}
